import { useState, useEffect, useCallback } from 'react'
import {
  Newspaper,
  RefreshCw,
  Trash2,
  ExternalLink,
  ChevronRight,
  ListChecks,
  Link2,
  Lightbulb,
  GitBranch,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plus,
  Globe,
  Search,
  Zap,
  ClipboardList,
} from 'lucide-react'
import { api, type BriefingActions, type BriefingActionItem } from '../lib/api'

interface BriefingSummary {
  id: string
  filename: string
  title: string
  itemCount: number
  actionCount: number
  createdAt: string | null
}

type ActionTab = 'punchlist' | 'wplinks' | 'ideas'

const TAB_META: Record<ActionTab, { label: string; icon: typeof ListChecks; color: string }> = {
  punchlist: { label: 'Punch List', icon: ListChecks, color: 'text-amber-500' },
  wplinks: { label: 'WP Links', icon: Link2, color: 'text-blue-500' },
  ideas: { label: 'Ideas', icon: Lightbulb, color: 'text-green-500' },
}

export function BriefingsPage() {
  const [briefings, setBriefings] = useState<BriefingSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [loadingHtml, setLoadingHtml] = useState(false)

  // Action items state
  const [actions, setActions] = useState<BriefingActions | null>(null)
  const [activeTab, setActiveTab] = useState<ActionTab>('punchlist')
  const [showActions, setShowActions] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncResults, setSyncResults] = useState<Array<{ type: string; id: string; status: string; message: string }> | null>(null)

  // Generate briefing state
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)

  // URL analysis state
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [analyzingUrl, setAnalyzingUrl] = useState(false)
  const [urlResult, setUrlResult] = useState<{
    headline: string
    summary: string
    sourceUrl: string
    sourceName: string
    suggestedPapers: string[]
    category: string
  } | null>(null)
  const [urlError, setUrlError] = useState<string | null>(null)

  // Impact analysis from URL
  const [runningImpact, setRunningImpact] = useState(false)
  const [impactResult, setImpactResult] = useState<{
    strategicValue: string
    valueVerdict: string
    paperAnalyses: Array<{
      paperId: string
      paperTitle: string
      priority: string
      valueToUs: string
      alignment: string[]
      gaps: string[]
      updateSuggestions: string[]
    }>
    ebookImpacts: Array<{
      ebookId: string
      ebookTitle: string
      relevant: boolean
      assessment: string
    }>
    newPaperRecommendation: {
      recommended: boolean
      title: string | null
      rationale: string | null
      suggestedCluster: string | null
    }
    overallAssessment: string
    crossReferences: string[]
  } | null>(null)

  const loadBriefings = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.briefing.list()
      setBriefings(data.briefings)
    } catch (err) {
      console.error('Failed to load briefings:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadBriefings()
  }, [loadBriefings])

  const selectBriefing = useCallback(async (id: string) => {
    setSelectedId(id)
    setLoadingHtml(true)
    setHtmlContent(null)
    setActions(null)
    setSyncResults(null)
    try {
      const [html, actionsData] = await Promise.all([
        api.briefing.getHtml(id),
        api.briefing.getActions(id),
      ])
      setHtmlContent(html)
      setActions(actionsData.actions)
    } catch (err) {
      console.error('Failed to load briefing:', err)
    } finally {
      setLoadingHtml(false)
    }
  }, [])

  const deleteAction = useCallback(async (tab: ActionTab, index: number) => {
    if (!selectedId || !actions) return
    try {
      await api.briefing.deleteAction(selectedId, tab, index)
      const updated = { ...actions }
      updated[tab] = [...updated[tab]]
      updated[tab].splice(index, 1)
      setActions(updated)
    } catch (err) {
      console.error('Failed to delete action:', err)
    }
  }, [selectedId, actions])

  const syncToTracker = useCallback(async () => {
    if (!selectedId) return
    setSyncing(true)
    setSyncResults(null)
    try {
      const result = await api.briefing.syncToTracker(selectedId)
      setSyncResults(result.results)
      const actionsData = await api.briefing.getActions(selectedId)
      setActions(actionsData.actions)
    } catch (err) {
      console.error('Failed to sync:', err)
    } finally {
      setSyncing(false)
    }
  }, [selectedId])

  const generateNewBriefing = useCallback(async () => {
    setGenerating(true)
    setGenError(null)
    try {
      const result = await api.briefing.generate()
      await loadBriefings()
      selectBriefing(result.briefingId)
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }, [loadBriefings, selectBriefing])

  const analyzeUrlSource = useCallback(async () => {
    if (!urlInput.trim()) return
    setAnalyzingUrl(true)
    setUrlError(null)
    setUrlResult(null)
    setImpactResult(null)
    try {
      const result = await api.briefing.analyzeUrl(urlInput.trim())
      setUrlResult(result.data)
    } catch (err) {
      setUrlError(err instanceof Error ? err.message : 'Analysis failed')
    } finally {
      setAnalyzingUrl(false)
    }
  }, [urlInput])

  const runImpactFromUrl = useCallback(async () => {
    if (!urlResult) return
    setRunningImpact(true)
    try {
      const result = await api.briefing.runImpactAnalysis({
        headline: urlResult.headline,
        summary: urlResult.summary,
        sourceUrl: urlResult.sourceUrl,
        sourceName: urlResult.sourceName,
        category: urlResult.category,
        paperIds: urlResult.suggestedPapers.length > 0 ? urlResult.suggestedPapers : undefined,
      })
      setImpactResult(result.data)
    } catch (err) {
      console.error('Impact analysis failed:', err)
    } finally {
      setRunningImpact(false)
    }
  }, [urlResult])

  // Toast for action feedback
  const [toast, setToast] = useState<string | null>(null)
  const showToast = useCallback((msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }, [])

  // Add impact analysis result to action items
  const addToActions = useCallback(async (
    tab: ActionTab,
    headline: string,
    summary: string,
    papers: string[],
    opts?: { sourceUrl?: string; sourceName?: string; category?: string; badge?: string }
  ) => {
    if (!selectedId) {
      showToast('Select a briefing first to save actions')
      return
    }

    const item: BriefingActionItem = {
      headline,
      summary,
      badge: opts?.badge || 'HIGH',
      date: new Date().toISOString().slice(0, 10),
      sourceUrl: opts?.sourceUrl || urlResult?.sourceUrl || '',
      sourceName: opts?.sourceName || urlResult?.sourceName || 'Impact Analysis',
      papers,
      theme: opts?.category || urlResult?.category || '',
      category: opts?.category || 'Impact Analysis',
      addedAt: new Date().toISOString(),
    }

    const current = actions || { punchlist: [], wplinks: [], ideas: [] }
    // Check duplicate
    if (current[tab].some((a) => a.headline === headline)) {
      showToast(`Already in ${TAB_META[tab].label}`)
      return
    }

    const updated = { ...current, [tab]: [...current[tab], item] }
    setActions(updated)

    try {
      await api.briefing.saveActions(selectedId, updated)
      showToast(`Added to ${TAB_META[tab].label}`)
    } catch (err) {
      console.error('Failed to save action:', err)
    }
  }, [selectedId, actions, urlResult, showToast])

  const actionList = actions ? actions[activeTab] : []
  const totalActions = actions
    ? actions.punchlist.length + actions.wplinks.length + actions.ideas.length
    : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Newspaper className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Intel Briefings</h1>
          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            {briefings.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowUrlInput(!showUrlInput); setUrlResult(null); setImpactResult(null); setUrlError(null) }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              showUrlInput
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'text-gray-600 dark:text-gray-300 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 hover:bg-gray-50 dark:hover:bg-surface-700'
            }`}
          >
            <Globe className="w-4 h-4" />
            Analyze URL
          </button>
          <button
            onClick={generateNewBriefing}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-colors"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                New Briefing
              </>
            )}
          </button>
          <button
            onClick={loadBriefings}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Generation Status */}
      {generating && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
          <RefreshCw className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Generating new briefing...</p>
            <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-0.5">Searching 7 categories via Perplexity, scoring against doctrine themes, generating HTML. This may take 2-3 minutes.</p>
          </div>
        </div>
      )}
      {genError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{genError}</p>
        </div>
      )}

      {/* URL Analysis Panel */}
      {showUrlInput && (
        <div className="p-4 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-xl space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && analyzeUrlSource()}
                placeholder="Paste a URL to analyze against doctrine papers..."
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <button
              onClick={analyzeUrlSource}
              disabled={analyzingUrl || !urlInput.trim()}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 dark:disabled:bg-surface-600 rounded-lg transition-colors"
            >
              {analyzingUrl ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Analyze
            </button>
          </div>

          {urlError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
              {urlError}
            </div>
          )}

          {analyzingUrl && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Fetching and analyzing URL content...
            </div>
          )}

          {urlResult && (
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 dark:bg-surface-900 rounded-lg border border-gray-100 dark:border-surface-700">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{urlResult.headline}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{urlResult.summary}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded">
                        {urlResult.category}
                      </span>
                      <span className="text-[10px] text-gray-400">{urlResult.sourceName}</span>
                    </div>
                  </div>
                </div>

                {urlResult.suggestedPapers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-surface-700">
                    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                      Matched Papers
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {urlResult.suggestedPapers.map((p) => (
                        <span key={p} className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {!impactResult && (
                <button
                  onClick={runImpactFromUrl}
                  disabled={runningImpact}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 rounded-lg transition-colors"
                >
                  {runningImpact ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Analyzing against full corpus...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Analyze Value to Doctrine
                    </>
                  )}
                </button>
              )}

              {/* Impact Analysis Results */}
              {impactResult && (
                <div className="space-y-3">
                  {/* Value Verdict Banner */}
                  <div className={`p-3 rounded-lg border ${
                    impactResult.valueVerdict === 'High Value' ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' :
                    impactResult.valueVerdict === 'Moderate Value' ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800' :
                    impactResult.valueVerdict === 'Low Value' ? 'bg-gray-50 dark:bg-surface-900 border-gray-200 dark:border-surface-700' :
                    'bg-gray-50 dark:bg-surface-900 border-gray-200 dark:border-surface-700'
                  }`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Doctrine Value Assessment</h4>
                      <span className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-md ${
                        impactResult.valueVerdict === 'High Value' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        impactResult.valueVerdict === 'Moderate Value' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        impactResult.valueVerdict === 'Low Value' ? 'bg-gray-100 text-gray-600 dark:bg-surface-700 dark:text-gray-400' :
                        'bg-gray-100 text-gray-500 dark:bg-surface-700 dark:text-gray-500'
                      }`}>
                        {impactResult.valueVerdict}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{impactResult.strategicValue}</p>
                  </div>

                  {/* Paper Analyses */}
                  {impactResult.paperAnalyses.length > 0 && (
                    <div>
                      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Affected Papers ({impactResult.paperAnalyses.length})
                      </p>
                      {impactResult.paperAnalyses.map((pa) => {
                        const paperSummary = pa.valueToUs || [
                          ...(pa.gaps?.length ? [`Gaps: ${pa.gaps[0]}`] : []),
                          ...(pa.updateSuggestions?.length ? [`Update: ${pa.updateSuggestions[0]}`] : []),
                          ...(pa.alignment?.length ? [`Aligns: ${pa.alignment[0]}`] : []),
                        ].join('. ').substring(0, 250)

                        return (
                        <div key={pa.paperId} className="p-3 mb-2 bg-gray-50 dark:bg-surface-900 rounded-lg border border-gray-100 dark:border-surface-700">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {pa.paperId}: {pa.paperTitle}
                            </span>
                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                              pa.priority === 'Critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              pa.priority === 'High' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                              pa.priority === 'Medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-gray-100 text-gray-600 dark:bg-surface-700 dark:text-gray-400'
                            }`}>
                              {pa.priority}
                            </span>
                          </div>

                          {pa.valueToUs && (
                            <p className="text-xs text-violet-600 dark:text-violet-400 mb-2 italic">{pa.valueToUs}</p>
                          )}

                          {pa.alignment?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] font-medium text-green-600 dark:text-green-400 uppercase mb-1">Validates</p>
                              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 pl-3">
                                {pa.alignment.map((a, i) => <li key={i} className="list-disc">{a}</li>)}
                              </ul>
                            </div>
                          )}
                          {pa.gaps?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400 uppercase mb-1">Gaps</p>
                              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 pl-3">
                                {pa.gaps.map((g, i) => <li key={i} className="list-disc">{g}</li>)}
                              </ul>
                            </div>
                          )}
                          {pa.updateSuggestions?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] font-medium text-blue-600 dark:text-blue-400 uppercase mb-1">Actions</p>
                              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 pl-3">
                                {pa.updateSuggestions.map((s, i) => <li key={i} className="list-disc">{s}</li>)}
                              </ul>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1.5 mt-3 pt-2.5 border-t border-gray-200 dark:border-surface-700">
                            <button
                              onClick={() => addToActions(
                                'punchlist',
                                `[${pa.priority}] Update ${pa.paperId}: ${pa.paperTitle}`,
                                paperSummary,
                                [pa.paperId],
                                { badge: pa.priority }
                              )}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors"
                            >
                              <ClipboardList className="w-3 h-3" />
                              Punch List
                            </button>
                            <button
                              onClick={() => addToActions(
                                'wplinks',
                                `Impact: ${urlResult?.headline || 'URL analysis'} on ${pa.paperId}`,
                                paperSummary,
                                [pa.paperId],
                                { badge: pa.priority }
                              )}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Link2 className="w-3 h-3" />
                              Link WP
                            </button>
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Ebook Impacts */}
                  {impactResult.ebookImpacts?.some((e) => e.relevant) && (
                    <div>
                      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Ebook Impact
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {impactResult.ebookImpacts.filter((e) => e.relevant).map((eb) => (
                          <div key={eb.ebookId} className="p-2 bg-violet-50 dark:bg-violet-900/10 rounded-lg border border-violet-200 dark:border-violet-800/30">
                            <p className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-0.5">{eb.ebookTitle}</p>
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-relaxed">{eb.assessment}</p>
                            <button
                              onClick={() => addToActions(
                                'punchlist',
                                `Update ebook: ${eb.ebookTitle}`,
                                eb.assessment,
                                [eb.ebookId],
                                { badge: 'MEDIUM' }
                              )}
                              className="flex items-center gap-1 mt-1.5 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded transition-colors"
                            >
                              <ClipboardList className="w-2.5 h-2.5" />
                              Punch List
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Paper Recommendation */}
                  {impactResult.newPaperRecommendation?.recommended && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-1">
                        <Lightbulb className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <p className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase">New Paper Recommended</p>
                      </div>
                      {impactResult.newPaperRecommendation.title && (
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{impactResult.newPaperRecommendation.title}</p>
                      )}
                      {impactResult.newPaperRecommendation.rationale && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{impactResult.newPaperRecommendation.rationale}</p>
                      )}
                      {impactResult.newPaperRecommendation.suggestedCluster && (
                        <span className="inline-block px-2 py-0.5 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded mb-2">
                          Cluster: {impactResult.newPaperRecommendation.suggestedCluster}
                        </span>
                      )}
                      <div className="flex gap-1.5 mt-2 pt-2 border-t border-green-200 dark:border-green-800/50">
                        <button
                          onClick={() => addToActions(
                            'ideas',
                            impactResult.newPaperRecommendation.title || `New paper from: ${urlResult?.headline}`,
                            impactResult.newPaperRecommendation.rationale || '',
                            [],
                            { badge: 'HIGH' }
                          )}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-green-600 dark:text-green-400 bg-white/50 dark:bg-surface-800/50 border border-green-200 dark:border-green-800/30 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                        >
                          <Lightbulb className="w-3 h-3" />
                          Add to Ideas
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Overall Assessment */}
                  {impactResult.overallAssessment && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-[10px] font-medium text-blue-600 dark:text-blue-400 uppercase mb-1">Overall Assessment</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{impactResult.overallAssessment}</p>

                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2.5 border-t border-blue-200 dark:border-blue-800/50">
                        <button
                          onClick={() => addToActions(
                            'punchlist',
                            `Review: ${urlResult?.headline || 'Impact assessment'}`,
                            impactResult.overallAssessment.substring(0, 250),
                            impactResult.paperAnalyses.map((p) => p.paperId),
                            { badge: 'HIGH' }
                          )}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-amber-600 dark:text-amber-400 bg-white/50 dark:bg-surface-800/50 border border-amber-200 dark:border-amber-800/30 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                        >
                          <ClipboardList className="w-3 h-3" />
                          Add All to Punch List
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Cross-References */}
                  {impactResult.crossReferences?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 mr-1">Also affected:</span>
                      {impactResult.crossReferences.map((cr, i) => (
                        <span key={i} className="px-1.5 py-0.5 text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 rounded">
                          {cr}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {briefings.length === 0 && !generating ? (
        <div className="text-center py-16">
          <Newspaper className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No briefings found.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Click <strong>New Briefing</strong> to generate one, or run <code className="px-1 py-0.5 bg-gray-100 dark:bg-surface-800 rounded">/intel-briefing</code> from Claude Code.
          </p>
        </div>
      ) : (
        <div className="flex gap-6">
          {/* Briefing List */}
          <div className={`${selectedId ? 'w-72 flex-shrink-0' : 'w-full max-w-3xl mx-auto'} space-y-2`}>
            {briefings.map((b) => (
              <button
                key={b.id}
                onClick={() => selectBriefing(b.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedId === b.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-sm'
                    : 'bg-white dark:bg-surface-800 border-gray-200 dark:border-surface-700 hover:border-blue-200 dark:hover:border-blue-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                    {b.title}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {b.createdAt && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(b.createdAt).toLocaleDateString()}
                    </span>
                  )}
                  <span>{b.itemCount} items</span>
                  {b.actionCount > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">{b.actionCount} actions</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Briefing Viewer */}
          {selectedId && (
            <div className="flex-1 min-w-0 space-y-4">
              {/* Toolbar */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {briefings.find((b) => b.id === selectedId)?.title || selectedId}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowActions(!showActions)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      showActions
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-white dark:bg-surface-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-surface-700 hover:bg-gray-50 dark:hover:bg-surface-700'
                    }`}
                  >
                    <ListChecks className="w-4 h-4" />
                    Actions {totalActions > 0 && `(${totalActions})`}
                  </button>
                  <a
                    href={`http://localhost:3003/api/briefing/${selectedId}/html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                {/* HTML Iframe */}
                <div className={`${showActions ? 'flex-1 min-w-0' : 'w-full'}`}>
                  {loadingHtml ? (
                    <div className="flex items-center justify-center h-[600px] bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  ) : htmlContent ? (
                    <iframe
                      srcDoc={htmlContent}
                      title="Briefing"
                      className="w-full h-[600px] rounded-xl border border-gray-200 dark:border-surface-700 bg-white"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-[600px] bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700">
                      <p className="text-gray-400">Failed to load briefing</p>
                    </div>
                  )}
                </div>

                {/* Actions Panel */}
                {showActions && (
                  <div className="w-96 flex-shrink-0 bg-white dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700 overflow-hidden flex flex-col h-[600px]">
                    {/* Tab Bar */}
                    <div className="flex border-b border-gray-200 dark:border-surface-700">
                      {(Object.keys(TAB_META) as ActionTab[]).map((tab) => {
                        const meta = TAB_META[tab]
                        const count = actions ? actions[tab].length : 0
                        return (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors ${
                              activeTab === tab
                                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            <meta.icon className={`w-3.5 h-3.5 ${meta.color}`} />
                            {meta.label}
                            {count > 0 && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-surface-700 rounded-full">
                                {count}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>

                    {/* Action Items */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {actionList.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                          No {TAB_META[activeTab].label.toLowerCase()} items
                        </div>
                      ) : (
                        actionList.map((item: BriefingActionItem, idx: number) => (
                          <div
                            key={`${item.headline}-${idx}`}
                            className="p-3 bg-gray-50 dark:bg-surface-900 rounded-lg border border-gray-100 dark:border-surface-700 group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {item.headline}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {item.summary}
                                </p>
                                {item.papers.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {item.papers.map((p) => (
                                      <span
                                        key={p}
                                        className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded"
                                      >
                                        {p}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => deleteAction(activeTab, idx)}
                                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Sync Footer */}
                    <div className="border-t border-gray-200 dark:border-surface-700 p-3 space-y-2">
                      {actions?.lastSynced && (
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">
                          Last synced: {new Date(actions.lastSynced).toLocaleString()}
                        </p>
                      )}

                      {syncResults && (
                        <div className="max-h-24 overflow-y-auto space-y-1">
                          {syncResults.map((r, i) => (
                            <div
                              key={i}
                              className={`flex items-center gap-1.5 text-xs ${
                                r.status === 'created' || r.status === 'linked'
                                  ? 'text-green-600 dark:text-green-400'
                                  : r.status === 'skipped'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {r.status === 'created' || r.status === 'linked' ? (
                                <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                              ) : (
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                              )}
                              <span className="truncate">{r.message}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={syncToTracker}
                        disabled={syncing || totalActions === 0}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-surface-600 rounded-lg transition-colors"
                      >
                        {syncing ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <GitBranch className="w-4 h-4" />
                            Sync to Tracker
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-gray-900 dark:bg-surface-700 text-white text-sm font-medium rounded-lg shadow-lg border border-gray-700 dark:border-surface-600 animate-in fade-in slide-in-from-bottom-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  )
}
